import * as React from 'react';
import { useSelector } from 'react-redux';
import { TeamOutlined } from '@ant-design/icons';
import Avatar from 'antd/lib/avatar';
import Tooltip from 'antd/lib/tooltip';
import { RootState } from '@/reducers/reducers';
import { selectActivePilotForRelease, selectCLIUsers, selectOrgGroups } from '@/selectors/selectors';
import { DefaultUserGroupId, TimelineEventChecklistItemDueUpcomingData } from '../../types';
import { displayTextForUserGroup, displayUserNameLong } from '../../helpers/displayHelpers';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';
import { OwnerAvatar, OwnerAvatarGroup, ReleasePilotAvatar } from '../Avatars';
import { useGroupTooltipContent } from '../checklistItems/GroupTooltip';

interface ChecklistItemOwnerAttributionProps {
    eventData: TimelineEventChecklistItemDueUpcomingData;
    orgId: string;
    appId: string;
    releaseId: string;
}

export const ChecklistItemOwnerAttribution: React.FC<ChecklistItemOwnerAttributionProps> = ({
    eventData,
    orgId,
    appId,
    releaseId,
}) => {
    const groups = eventData?.groups ?? [];
    const hasPilotGroup = groups.includes(DefaultUserGroupId.Pilot);

    const activeReleasePilotUser = useSelector((state: RootState) =>
        selectActivePilotForRelease(state, orgId, releaseId),
    );
    const owners = (eventData?.owners ?? []).filter(
        (ownerId) => !(hasPilotGroup && ownerId === activeReleasePilotUser?.id),
    );

    const ownerUsers = useSelector((state: RootState) => selectCLIUsers(state, orgId, appId, owners));
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const groupNames = groups
        .map((groupId) => orgGroups[groupId])
        .filter(Boolean)
        .map(displayTextForUserGroup);

    const ownerEntries = owners.map((ownerId) => {
        const user = ownerUsers.find((candidate) => candidate.id === ownerId);
        return { user, label: user ? displayUserNameLong(user) : ownerId };
    });
    const ownerNames = ownerEntries.map((entry) => entry.label);
    const allOwnersResolved = ownerEntries.every((entry) => entry.user);

    const soleGroup = groups.length === 1 && ownerEntries.length === 0 ? orgGroups[groups[0]] : undefined;
    const soleGroupContent = useGroupTooltipContent(soleGroup, appId, releaseId);

    if (groups.length === 1 && hasPilotGroup && ownerEntries.length === 0) {
        return <ReleasePilotAvatar />;
    }

    if (groups.length === 0 && ownerEntries.length > 0) {
        if (ownerEntries.length === 1) {
            const [entry] = ownerEntries;
            return (
                <Tooltip title={entry.label} placement="top">
                    <div style={{ display: 'flex' }}>
                        {entry.user ?
                            <OwnerAvatar user={entry.user} />
                        :   <Avatar
                                size={25}
                                style={{
                                    fontSize: 12,
                                    border: 'none',
                                    backgroundColor: getAvatarColorForName(entry.label),
                                    flexShrink: 0,
                                }}
                            >
                                {entry.label.charAt(0).toUpperCase()}
                            </Avatar>
                        }
                    </div>
                </Tooltip>
            );
        }
        if (allOwnersResolved) {
            return <OwnerAvatarGroup maxAvatars={3} owners={ownerUsers} />;
        }
    }

    const allNames = [...ownerNames, ...groupNames];
    if (allNames.length === 0) {
        return null;
    }

    const soleGroupListsMembers = typeof soleGroupContent === 'object' && soleGroupContent !== null;

    const tooltip =
        soleGroup && soleGroupContent ?
            <>
                <div style={{ fontWeight: soleGroupListsMembers ? 600 : 400 }}>
                    {displayTextForUserGroup(soleGroup)}
                </div>
                {soleGroupContent}
            </>
        : allNames.length === 1 ? allNames[0]
        : <ul style={{ margin: 0, paddingLeft: 16 }}>
                {allNames.map((name) => (
                    <li key={name}>{name}</li>
                ))}
            </ul>;

    return (
        <Tooltip title={tooltip} placement="top">
            <TeamOutlined style={{ fontSize: 18, color: antGrey.grey7 }} />
        </Tooltip>
    );
};
